import { useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNotifications } from '../../context/NotificationContext';
import NotificationDropdown from '../notifications/NotificationDropdown';
import {
  Search, PlusSquare, User, Settings, LogOut, Sun, Moon,
  DollarSign, Coins, Megaphone, Bot, Bell, Bookmark, Shield, Vault,
} from 'lucide-react';
import {
  HearthIcon, WayfinderIcon, SagasIcon, TheRealmsIcon,
  RavenpostIcon, TheHeraldIcon, KeepsakeIcon,
} from '../icons/NorseIcons';
import { RealmsNav } from '../social/socialModules';
import BrandMark from '../brand/BrandMark';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import CreatePostModal from '../post/CreatePostModal';
import DesktopRightRail from './DesktopRightRail';

const RIGHT_RAIL_HIDDEN = ['/messages', '/reels', '/odin', '/world'];

export default function MainLayout({ children, hideNav = false }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const bellRef = useRef(null);
  const toggleNotifDropdown = useCallback(() => setShowNotifDropdown(v => !v), []);

  const navItems = [
    { icon: HearthIcon,    label: 'The Hearth',   path: '/home' },
    { icon: WayfinderIcon, label: 'Wayfinder',    path: '/explore' },
    { icon: SagasIcon,     label: 'Sagas',        path: '/reels' },
    { icon: TheRealmsIcon, label: 'The Realms',   path: '/world', special: true },
    { icon: RavenpostIcon, label: 'Ravenpost',    path: '/messages' },
    { icon: TheHeraldIcon, label: 'The Herald',   path: '/notifications' },
  ];

  const isActive = (path) => location.pathname === path;
  const showRightRail = !RIGHT_RAIL_HIDDEN.some((p) => location.pathname.startsWith(p));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Header */}
      <header className="sticky top-0 z-40 w-full backdrop-blur-xl bg-background/80 border-b border-[hsl(var(--mg-steel)/0.15)] mg-safe-top">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          {/* Logo */}
          <Link to="/home" className="flex items-center gap-2.5" data-testid="brand-logo-link">
            <BrandMark size={34} />
            <span className="mg-wordmark text-base text-foreground" style={{ letterSpacing: '0.12em' }}>Midgard</span>
          </Link>

          {/* Search Bar - Desktop */}
          <div className="hidden min-[900px]:flex flex-1 max-w-md mx-8">
            <Link 
              to="/search" 
              className="w-full flex items-center gap-3 px-4 py-2 bg-[hsl(var(--mg-surface)/0.5)] border border-[hsl(var(--mg-steel)/0.2)] rounded-lg text-muted-foreground hover:border-[hsl(var(--mg-steel)/0.35)] transition-colors"
            >
              <Search className="w-4 h-4" />
              <span className="text-sm">Search Midgard...</span>
            </Link>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-1.5">
            {/* Search - Mobile */}
            <Link
              to="/search"
              className="min-[900px]:hidden mg-btn-icon w-9 h-9"
              data-testid="mobile-search-btn"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </Link>

            {/* Runegold Display */}
            <Link 
              to="/wallet" 
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[hsl(var(--mg-gold)/0.08)] border border-[hsl(var(--mg-gold-deep)/0.25)] hover:bg-[hsl(var(--mg-gold)/0.14)] transition-colors"
              data-testid="coins-display"
              title="Runegold Wallet"
            >
              <Coins className="w-4 h-4 text-[hsl(var(--mg-gold))]" />
              <span className="text-sm font-semibold text-foreground">{user?.wonder_coins || 0}</span>
            </Link>

            {/* Create Post */}
            <Button 
              onClick={() => setShowCreatePost(true)}
              className="mg-btn mg-btn-primary px-4 py-2 text-sm hidden sm:flex items-center gap-2"
              data-testid="create-post-btn"
            >
              <PlusSquare className="w-4 h-4" />
              <span className="hidden lg:inline">Create</span>
            </Button>

            {/* Theme Toggle */}
            <button 
              onClick={toggleTheme}
              className="mg-btn-icon w-9 h-9 rounded-lg"
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Notification Bell */}
            <div className="relative">
              <button
                ref={bellRef}
                onClick={toggleNotifDropdown}
                className="mg-btn-icon w-9 h-9 rounded-lg relative"
                data-testid="header-notification-bell"
                aria-label="Notifications"
              >
                <Bell className="w-4.5 h-4.5" />
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-[hsl(var(--mg-purple))] text-[9px] font-bold text-white px-0.5 border-2 border-background"
                    data-testid="notification-badge"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              <NotificationDropdown
                open={showNotifDropdown}
                onClose={() => setShowNotifDropdown(false)}
                triggerRef={bellRef}
              />
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-lg p-0.5 hover:bg-muted/50 transition-colors" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8 border border-[hsl(var(--mg-steel)/0.3)]">
                    <AvatarImage src={user?.avatar} alt={user?.display_name} />
                    <AvatarFallback className="text-xs">{user?.display_name?.[0]}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-card border-[hsl(var(--mg-steel)/0.25)] rounded-lg">
                <DropdownMenuItem onClick={() => navigate(`/profile/${user?.username}`)} data-testid="profile-menu-item">
                  <User className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/bookmarks')} data-testid="bookmarks-menu-item">
                  <Bookmark className="w-4 h-4 mr-2" /> Bookmarks
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/creator-dashboard')} data-testid="earnings-menu-item">
                  <DollarSign className="w-4 h-4 mr-2" /> Creator Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/wallet')} data-testid="wallet-menu-item">
                  <Coins className="w-4 h-4 mr-2 text-[hsl(var(--mg-gold))]" /> Runegold Wallet
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/ads')} data-testid="ads-menu-item">
                  <Megaphone className="w-4 h-4 mr-2" /> Advertising
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`pb-24 min-[900px]:pb-8 ${showRightRail ? 'min-[1100px]:mr-[260px] min-[1200px]:mr-80' : ''}`}>
        {children}
      </main>

      {/* Bottom Navigation - Mobile */}
      {!hideNav && (
        <nav
          className="fixed left-1/2 -translate-x-1/2 w-[92%] max-w-md bg-card/90 backdrop-blur-2xl border border-[hsl(var(--mg-steel)/0.2)] rounded-2xl px-3 py-2.5 flex justify-between items-center shadow-2xl z-50 min-[900px]:hidden"
          style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`p-2.5 rounded-lg transition-all relative ${
                item.special
                  ? 'bg-[hsl(var(--mg-purple)/0.1)] border border-[hsl(var(--mg-rune)/0.35)] text-[hsl(var(--mg-purple))]'
                  : isActive(item.path) 
                    ? 'bg-[hsl(var(--mg-purple))] text-white' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon className="w-5 h-5" />
              {item.path === '/notifications' && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-[hsl(var(--mg-purple))] text-[8px] font-bold text-white px-0.5 border-2 border-card">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
          ))}
          <button
            onClick={() => setShowCreatePost(true)}
            className="p-2.5 rounded-lg bg-[hsl(var(--mg-purple))] text-white"
            data-testid="nav-create"
          >
            <PlusSquare className="w-5 h-5" />
          </button>
        </nav>
      )}

      {/* Sidebar Navigation - Desktop */}
      <aside className="fixed left-0 top-14 h-[calc(100vh-3.5rem)] w-60 border-r border-[hsl(var(--mg-steel)/0.12)] bg-background/90 backdrop-blur-xl hidden min-[900px]:flex flex-col p-3 z-30 overflow-y-auto scrollbar-hide">
        <nav className="flex-none space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-all ${
                item.special
                  ? 'bg-[hsl(var(--mg-purple)/0.08)] border border-[hsl(var(--mg-rune)/0.3)] text-foreground hover:border-[hsl(var(--mg-rune)/0.5)]'
                  : isActive(item.path)
                    ? 'bg-[hsl(var(--mg-purple))] text-white font-semibold'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
              data-testid={`sidebar-${item.path.slice(1)}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {item.special && (
                <span className="ml-auto text-[9px] uppercase tracking-wider text-[hsl(var(--mg-purple))] opacity-60 font-semibold">Realms</span>
              )}
              {item.path === '/notifications' && unreadCount > 0 && (
                <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-md bg-[hsl(var(--mg-purple))] text-[9px] font-bold text-white px-1" data-testid="sidebar-notification-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          ))}
          <Link
            to="/odin"
            className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-all ${
              isActive('/odin')
                ? 'bg-[hsl(var(--mg-purple))] text-white font-semibold'
                : 'bg-[hsl(var(--mg-purple)/0.08)] border border-[hsl(var(--mg-rune)/0.2)] text-foreground hover:border-[hsl(var(--mg-rune)/0.4)]'
            }`}
            data-testid="sidebar-odin"
          >
            <Bot className="w-5 h-5 text-[hsl(var(--mg-purple))]" />
            <span>oDIN AI</span>
            <span className="ml-auto text-[8px] uppercase tracking-wider text-[hsl(var(--mg-purple))] opacity-50 font-semibold">AI</span>
          </Link>
          <Link
            to="/bookmarks"
            className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-all ${
              isActive('/bookmarks')
                ? 'bg-[hsl(var(--mg-purple))] text-white font-semibold'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
            data-testid="sidebar-bookmarks"
          >
            <KeepsakeIcon className="w-5 h-5" />
            <span>Keepsakes</span>
          </Link>

          {(user?.role === 'moderator' || user?.role === 'admin') && (
            <Link
              to="/moderation"
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-all ${
                isActive('/moderation')
                  ? 'bg-amber-600 text-white font-semibold'
                  : 'text-amber-600/80 hover:text-amber-600 hover:bg-amber-600/8 border border-amber-600/20 hover:border-amber-600/40'
              }`}
              data-testid="sidebar-moderation"
            >
              <Shield className="w-5 h-5" />
              <span>Wardens' Hall</span>
            </Link>
          )}
          {user?.role === 'admin' && (
            <Link
              to="/treasury"
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-all ${
                isActive('/treasury')
                  ? 'bg-[hsl(var(--mg-gold))] text-black font-semibold'
                  : 'text-[hsl(var(--mg-gold)/0.8)] hover:text-[hsl(var(--mg-gold))] border border-[hsl(var(--mg-gold-deep)/0.25)] hover:border-[hsl(var(--mg-gold-deep)/0.5)]'
              }`}
              data-testid="sidebar-treasury"
            >
              <Vault className="w-5 h-5" />
              <span>Treasury</span>
            </Link>
          )}
        </nav>

        <RealmsNav />

        {/* User Card */}
        <div className="mt-auto pt-3">
          <Link 
            to={`/profile/${user?.username}`}
            className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/40 transition-colors"
          >
            <Avatar className="w-9 h-9 border border-[hsl(var(--mg-steel)/0.25)]">
              <AvatarImage src={user?.avatar} alt={user?.display_name} />
              <AvatarFallback className="text-xs">{user?.display_name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{user?.display_name}</p>
              <p className="text-xs text-muted-foreground truncate">@{user?.username}</p>
            </div>
          </Link>
        </div>
      </aside>

      {/* Desktop Right Rail */}
      {showRightRail && <DesktopRightRail />}

      {/* Create Post Modal */}
      <CreatePostModal 
        open={showCreatePost} 
        onOpenChange={setShowCreatePost}
      />
    </div>
  );
}
